import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import {
  Home, ChevronRight, Megaphone, CalendarDays, FileText, Paperclip,
  ExternalLink, ArrowRight, UserRound, Crown, Users, Boxes, Info, MessageCircle,
} from 'lucide-react';
import {
  fetchUnitAnggota,
  fetchPengumumanUnit,
  fetchUnitMemberProfile,
  getImageUrl,
  formatTanggalIndonesia,
} from '../utils/api';

const UNIT = 'bauk';

const services = [
  {
    icon: Users,
    title: 'Kepegawaian',
    description: 'Administrasi kepegawaian dosen dan tenaga kependidikan STIKES Harapan Ibu Jambi.',
  },
  {
    icon: Boxes,
    title: 'Perlengkapan',
    description: 'Pengelolaan sarana, prasarana, dan perlengkapan penunjang kegiatan kampus.',
  },
  {
    icon: FileText,
    title: 'Administrasi Umum',
    description: 'Layanan kesekretariatan dan administrasi umum institusi.',
  },
];

function MemberCard({ member }) {
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    if (!member.profileSlug) return undefined;
    let active = true;
    fetchUnitMemberProfile(member.profileSlug, member.profileType)
      .then((result) => {
        if (active && result?.profile?.foto) setPhoto(getImageUrl(result.profile.foto, { width: 240, height: 300, quality: 85 }));
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [member.profileSlug, member.profileType]);

  const initials = member.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      data-testid={`bauk-member-${member.id}`}
      className={`bg-white dark:bg-[#1A182E] rounded-2xl border shadow-sm hover:shadow-lg transition-all duration-300 p-6 ${
        member.isHead
          ? 'border-amber-300 dark:border-amber-400/30 border-l-4 border-l-amber-500 dark:border-l-amber-400'
          : 'border-gray-100 dark:border-gray-800'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="w-16 h-20 rounded-xl overflow-hidden bg-purple-50 dark:bg-purple-900/30 flex-shrink-0 grid place-items-center">
          {photo ? (
            <img src={photo} alt={member.name} loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-[#7B2FBE]">{initials}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {member.isHead && (
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-400 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-amber-950 shadow-sm dark:border-amber-300/40 dark:bg-amber-300 dark:text-amber-950">
              <Crown size={10} /> Kepala Unit
            </span>
          )}
          <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading text-sm leading-tight">{member.name}</h3>
          <p className="text-xs text-[#7B2FBE] dark:text-purple-300 mt-0.5">{member.position}</p>

          {member.tasks?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {member.tasks.map((task, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <ArrowRight size={12} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
                  {task}
                </li>
              ))}
            </ul>
          )}

          {(member.profileSlug || member.whatsapp) && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              {member.profileSlug && (
                <Link
                  to={`/${member.profileType || 'tendik'}/${member.profileSlug}`}
                  className="inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] hover:underline"
                >
                  <UserRound size={12} /> Lihat profil
                </Link>
              )}
              {member.whatsapp && (
                <a
                  href={`https://wa.me/${member.whatsapp}?text=${encodeURIComponent(`Assalamualaikum, saya ingin menghubungi ${member.name} (${member.position}) terkait layanan BAUK STIKES Harapan Ibu Jambi.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  <MessageCircle size={12} /> Hubungi Kami
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function AnnouncementCard({ item }) {
  const thumbUrl = getImageUrl(item.thumbnail, { width: 640, height: 320 });
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="bg-white dark:bg-[#1A182E] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
    >
      {thumbUrl && (
        <Link to={`/berita/${item.slug}`} className="block h-40 overflow-hidden">
          <img
            src={thumbUrl}
            alt={item.judul}
            loading="lazy"
            style={{ objectPosition: item.thumbnailPosition || '50% 50%' }}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </Link>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mb-2">
          <CalendarDays size={12} /> {formatTanggalIndonesia(item.tanggal_publikasi)}
          {item.internal && (
            <span className="ml-auto rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[10px] font-semibold">
              Khusus halaman unit
            </span>
          )}
        </div>
        <Link to={`/berita/${item.slug}`} className="group block">
          <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading text-sm leading-snug group-hover:text-[#7B2FBE] transition-colors">
            {item.judul}
          </h3>
        </Link>
        {item.Excerpt && (
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3">{item.Excerpt}</p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 dark:border-gray-800 pt-3">
          <Link to={`/berita/${item.slug}`} className="inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] font-medium hover:underline">
            Baca selengkapnya <ArrowRight size={12} />
          </Link>
          {item.lampiranUrl && (
            <a href={item.lampiranUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-[#7B2FBE]">
              <Paperclip size={12} /> {item.lampiranNama || 'Lampiran'}
            </a>
          )}
          {item.tautan && (
            <a href={item.tautan} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-[#7B2FBE]">
              <ExternalLink size={12} /> Tautan terkait
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const Bauk = () => {
  const [members, setMembers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    Promise.all([
      fetchUnitAnggota(UNIT).catch(() => []),
      fetchPengumumanUnit(UNIT).catch(() => []),
    ]).then(([memberData, announcementData]) => {
      if (!active) return;
      setMembers(Array.isArray(memberData) ? memberData : []);
      setAnnouncements(Array.isArray(announcementData) ? announcementData : []);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="bauk-page">
      <SEO
        title="BAUK — Bagian Administrasi Umum & Kepegawaian"
        description="Unit BAUK STIKES Harapan Ibu Jambi: layanan kepegawaian, perlengkapan, administrasi umum, serta pengumuman resmi."
      />

      <div className="page-hero pt-[70px]">
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
              <Link to="/" className="flex items-center gap-1 transition-colors hover:text-white">
                <Home size={14} /> Beranda
              </Link>
              <ChevronRight size={14} />
              <span>Unit</span>
              <ChevronRight size={14} />
              <span className="text-white">BAUK</span>
            </div>
            <h1 className="mb-4 font-heading text-4xl font-bold text-white md:text-5xl">BAUK</h1>
            <p className="max-w-2xl text-base text-white/80">
              Bagian Administrasi Umum & Kepegawaian STIKES Harapan Ibu Jambi — melayani kepegawaian,
              perlengkapan, dan administrasi umum kampus.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {services.map((service) => (
              <div key={service.title} className="bg-white dark:bg-[#1A182E] rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] grid place-items-center mb-3">
                  <service.icon size={18} />
                </div>
                <h3 className="font-bold text-[#1A1A2E] dark:text-white font-heading text-sm mb-1.5">{service.title}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>

          <section className="mb-12">
            <h2 className="font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white mb-1.5">Struktur & Pembagian Tugas</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Anggota BAUK beserta bidang tugas masing-masing.
            </p>
            {loading ? (
              <div className="grid min-h-40 place-items-center text-sm text-gray-400">Memuat…</div>
            ) : members.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 py-12 text-center text-sm text-gray-500">
                Data anggota unit belum tersedia.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {members.map((member) => <MemberCard key={member.id} member={member} />)}
              </div>
            )}
          </section>

          <section className="mb-12">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white mb-1.5">Pengumuman BAUK</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Informasi resmi terbaru dari Bagian Administrasi Umum & Kepegawaian.
                </p>
              </div>
              <Link to="/berita" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#7B2FBE] font-medium hover:underline whitespace-nowrap">
                Semua berita <ArrowRight size={14} />
              </Link>
            </div>
            {loading ? (
              <div className="grid min-h-40 place-items-center text-sm text-gray-400">Memuat…</div>
            ) : announcements.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-700 py-12 text-center">
                <Megaphone className="mx-auto text-gray-300 dark:text-gray-600" size={36} />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Belum ada pengumuman dari BAUK.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {announcements.map((item) => <AnnouncementCard key={item.id} item={item} />)}
              </div>
            )}
          </section>

          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-2xl p-6 flex items-start gap-4">
            <Info size={20} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-[#7B2FBE] dark:text-purple-300 mb-2 font-heading">Butuh Bantuan Layanan Umum & Kepegawaian?</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                Untuk pertanyaan seputar kepegawaian, perlengkapan, atau administrasi umum, hubungi BAUK
                STIKES Harapan Ibu Jambi.
              </p>
              <Link
                to="/kontak"
                className="inline-flex items-center gap-2 bg-[#7B2FBE] text-white px-5 py-2 rounded-xl text-sm font-medium hover:bg-[#9333EA] transition-colors"
              >
                Hubungi Kami <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bauk;
